'use client';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useMemo, useState } from 'react';

import type { ColorMode } from '../contexts';
import type { PaletteMode } from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';

export type UseColorMode = (
  initialMode?: PaletteMode,
  queryInput?: string | ((theme: unknown) => string)
) => {
  mode: PaletteMode;
  setMode: Dispatch<SetStateAction<PaletteMode>>;
  colorMode: ColorMode;
};

export const useColorMode: UseColorMode = (
  initialMode = 'light',
  queryInput = '(prefers-color-scheme: dark)'
) => {
  const [mode, setMode] = useState<PaletteMode>( initialMode );
  const [isInPreferenceMode, setIsInPreferenceMode] = useState( false );
  const prefersDarkMode = useMediaQuery( queryInput );

  useEffect( () => {
    if (isInPreferenceMode) {
      setMode( prefersDarkMode ? 'dark' : 'light' );
    }
  }, [isInPreferenceMode, prefersDarkMode] );

  const colorMode: ColorMode = useMemo(
    () => ({
      // The dark mode switch would invoke this method
      toggleColorMode: () => {
        setMode((prevMode: PaletteMode) => prevMode === 'light' ? 'dark' : 'light');
      },
      togglePreferenceMode: () => {
        setIsInPreferenceMode((prevMode) => !prevMode);
      },
      setIsInPreferenceMode,
      setMode,
    }),
    []
  );

  return { mode, setMode, colorMode };
};
